import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Row, Col, Select, Switch, Button, Dropdown, Modal } from "antd";
import toast from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import { getApi } from "redux/sagas/getApiDataSaga";
import { SERVER_IP } from "assets/Config";
import HighlightComponent from "components/HighlightComponent";
import { resetApiStatus } from "redux/reducers/globals/globalActions";
import { generatePagination } from "helpers";
import { API_STATUS, USER_TYPE } from "constants/app-constants";
import { postApi } from "redux/sagas/postApiDataSaga";
import { sendPostRequest } from "redux/sagas/utils";
import UserListPresentational from "./user-list-presentational";
import { EditOutlined,
	KeyOutlined,
 } from "@ant-design/icons";
import EditPasswordModal from "pages/users/edit-password";


const items = [
	{
		label: 'Edit Details',
		key: 'edit',
		icon: <EditOutlined />,
	},
	{
		label: 'Edit Password',
		key: 'password',
		icon: <KeyOutlined />,
	},
];

const initialPageSize = 10;
const intialPageSizeOptions = [10, 15, 20];

const { confirm } = Modal;

const UserListFunctional = React.memo(() => {
	const globalRedux = useSelector((state) => state.globalRedux);
	const loginUser = useSelector((state) => state.loginRedux);
	const users = useSelector((state) => state?.globalRedux?.users);
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [userAddModal, setUserAddModal] = useState(false);
	const [userEditModal, setUserEditModal] = useState(false);
	const [searchKey, setSearchKey] = useState("");
	const [tableData, setTableData] = useState(users);
	const [editUser, setEditUser] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(initialPageSize);
	const [showPasswordModal, setShowPasswordModal] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);

	const dispatch = useDispatch();

	const getUsers = useCallback(() => {
		let url = `${SERVER_IP}user?orgId=${globalRedux.selectedOrganization._id}`;
		dispatch(getApi("GET_USERS", url));
	}, [dispatch, globalRedux.selectedOrganization._id]);

	useEffect(() => {
		getUsers();
	}, [getUsers]);

	useEffect(() => {
		generatePagination(tableData);
	}, [tableData]);

	useEffect(() => {
		let doIt = false;
		if (globalRedux.apiStatus.DELETE_USER === "SUCCESS") {
			dispatch(resetApiStatus("DELETE_USER"));
			setSelectedRowKeys([]);
			doIt = true;
		}
		if (doIt) {
			getUsers();
		}
		if (globalRedux.apiStatus.MODIFY_USER_ROLE === "SUCCESS") {
			dispatch(resetApiStatus("MODIFY_USER_ROLE"));
			getUsers?.();
		}
	}, [
		globalRedux.apiStatus.DELETE_USER,
		globalRedux.apiStatus.MODIFY_USER_ROLE,
		dispatch,
		getUsers,
	]);

	useEffect(() => {
		setTableData(users);
	}, [users]);

	const filteredData = useMemo(() => {
		if (searchKey === "") {
			return tableData;
		}
		return tableData.filter((user) => {
			return (
				(user?.userNumber || "")
					?.toString()
					?.toLowerCase()
					.includes(searchKey.toLowerCase()) ||
				(user?.firstName || "")
					?.toLowerCase()
					.includes(searchKey.toLowerCase()) ||
				(user?.lastName || "")
					?.toLowerCase()
					.includes(searchKey.toLowerCase()) ||
				(user?.mobile || "")?.toLowerCase().includes(searchKey.toLowerCase())
			);
		});
	}, [tableData, searchKey]);

	const handleDrawer = (rowData) => {
		setEditUser(rowData);
		setUserEditModal(true);
	};

	const handleModifyRole = async (rowData, value) => {
		let data = {
			orgId: globalRedux?.selectedOrganization?._id,
			userId: loginUser?.id,
			accessLevel: value || "",
			staffId: rowData?._id,
		};
		dispatch(postApi(data, "MODIFY_USER_ROLE"));
	};

	const handleAddUser = () => {
		setEditUser(null);
		setUserAddModal(true);
	};

	const handleClick = (value, row) => {
		confirm({
			title: `${row?.firstName} ${row?.lastName}`,
			content: (
				<div>
					Are you sure you want to{" "}
					<span style={{ fontWeight: "bold", textDecoration: "underline" }}>
						{value ? "Deactivate" : "Activate"}
					</span>{" "}
					this user?
				</div>
			),
			okText: "Yes",
			cancelText: "No",
			onOk() {
				console.log(true);
				sendPostRequest({
					url: `${SERVER_IP}user/updatestatus`,
					body: {
						orgId: globalRedux.selectedOrganization._id,
						userId: loginUser?.id,
						staffId: row?.userId,
						isActive: !value,
					},
				})
					.then((res) => {
						if (res?.error?.response?.data?.code) {
							toast.error(
								res?.error?.response?.data?.message || "Something went wrong!"
							);
						} else {
							getUsers();
						}
					})
					.catch((err) => {
						// console.log('🚀 ~ file: user-list-functional.js:124 ~ onOk ~ err:', err);
					});
			},
		});
	};
	const column = [
		{
			title: "First Name",
			dataIndex: "firstName",
			key: "firstName",
			width: "15%",
			sorter: (a, b) => a?.firstName?.localeCompare(b?.firstName),
			render: (value, row) => (
				<HighlightComponent
					highlightClassName="highlightClass"
					searchWords={[searchKey]}
					autoEscape={true}
					textToHighlight={value}
				/>
			),
		},
		{
			title: "Last Name",
			dataIndex: "lastName",
			key: "lastName",
			width: "15%",
			sorter: (a, b) => a?.lastName?.localeCompare(b?.lastName),
			render: (value, row) => (
				<HighlightComponent
					highlightClassName="highlightClass"
					searchWords={[searchKey]}
					autoEscape={true}
					textToHighlight={value}
				/>
			),
		},
		{
			title: "Mobile",
			dataIndex: "mobile",
			key: "mobile",
			width: "15%",
			sorter: (a, b) => a?.mobile - b?.mobile,
			render: (value) => (
				<HighlightComponent
					searchWords={[searchKey]}
					autoEscape={true}
					textToHighlight={value}
				/>
			),
		},
		{
			title: "Email",
			dataIndex: "email",
			key: "email",
			width: "20%",
			sorter: (a, b) => a?.email?.localeCompare(b?.email),
			render: (value) => (
				<HighlightComponent
					searchWords={[searchKey]}
					autoEscape={true}
					textToHighlight={value}
				/>
			),
		},
		{
			title: "Status",
			dataIndex: "isActive",
			key: "isActive",
			width: "10%",
			align: "center",
			render: (value) => (value ? "Active" : "Inactive"),
		},
		{
			title: "Access Level",
			dataIndex: "accessLevel",
			key: "accessLevel",
			width: "15%",
			sorter: (a, b) => a?.accessLevel?.localeCompare(b?.accessLevel),
			render: (value) => value,
		},
		{
			title: "More",
			key: "actions",
			dataIndex: "actions",
			width: "10%",
			align: "center",
			render: (value, row) => {
				const handleMenuClick = ({ key }) => {
					if (key === 'edit') {
						handleDrawer(row);
					} else if (key === 'password') {
						setSelectedUser(row);
						setShowPasswordModal(true);
					}
				};
				
				// const handleMenuClick = ({ key }) => {
				// 	if (key === "edit") {
				// 		handleDrawer(row);
				// 	} else if (key === "password") {
				// 		Modal.info({
				// 			title: `Edit Password for ${row.firstName} ${row.lastName}`,
				// 			content: "This feature is under development.",
				// 		});
				// 	}
				// };
	
				const items = [
					{ label: "Edit Details", key: "edit" },
					{ label: "Edit Password", key: "password" },
				];
	
				return (
					<Dropdown menu={{ items, onClick: handleMenuClick }}>
						<Button onClick={(e) => e.preventDefault()}>Action</Button>
					</Dropdown>
				);
			},
		},
	];
	

	const handleTableChange = (currentPage, pageSize) => {
		setCurrentPage(currentPage);
		setPageSize(pageSize);
	};

	const getStartingValue = () => {
		if (currentPage === 1) return 1;
		else {
			return (currentPage - 1) * pageSize + 1;
		}
	};

	const getEndingValue = () => {
		if (currentPage === 1)
			return tableData.length < pageSize ? tableData.length : pageSize;
		else {
			let end = currentPage * pageSize;
			return end > tableData.length ? tableData.length : end;
		}
	};

	const tableLoading = useMemo(
		() =>
			globalRedux.apiStatus.GET_USERS === API_STATUS.PENDING ||
			globalRedux.apiStatus.MODIFY_USER_ROLE === API_STATUS.PENDING,
		[globalRedux?.apiStatus?.GET_USERS, globalRedux.apiStatus.MODIFY_USER_ROLE]
	);

	const onSelectChange = (selectedRowKeys) => {
		setSelectedRowKeys(selectedRowKeys);
	};

	const rowSelection = {
		selectedRowKeys,
		onChange: onSelectChange,
		columnWidth: "5%",
		// fixed: true,
	};

	const handleClose = useCallback(() => {
		setUserAddModal(false);
		setUserEditModal(false);
		setEditUser(null);
	}, [setUserAddModal, setEditUser]);

	const selectedRowKey = selectedRowKeys?.[0];

	const userDetail = useMemo(() => {
		if (selectedRowKey) {
			return filteredData?.find((data) => data?._id === selectedRowKey);
		}
		return null;
	}, [selectedRowKey, filteredData]);
	// console.log("🚀 ~ file: user-list-functional.js:239 ~ userDetail ~ userDetail:", userDetail, selectedRowKeys)

	return (
		<>
		<UserListPresentational
			{...{
				filteredData,
				column,
				tableLoading,
				rowSelection,
				selectedRowKeys,
				handleAddUser,
				currentPage,
				pageSize,
				intialPageSizeOptions,
				initialPageSize,
				handleTableChange,
				setSearchKey,
				getStartingValue,
				getEndingValue,
				userAddModal,
				setUserAddModal,
				userEditModal,
				setUserEditModal,
				refreshList: getUsers,
				editUser,
				handleClose,
				userDetail,
			}}
		/>
		<EditPasswordModal
	open={showPasswordModal}
	onClose={() => setShowPasswordModal(false)}
	userData={selectedUser}
	refreshList={getUsers}
/>

		</>
	);
});

export default UserListFunctional;
